import KeyMomentChoiceContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/KeyMomentChoiceContent";
import HubEvent from "../../hub/event/HubEvent";
import KeyDecisionEvent from "../event/KeyDecisionEvent";
import KeyDecision from "./KeyDecision";
import KeyDecisionAnswer from "./KeyDecisionAnswer";
import KeyDecisionsConfig from "./KeyDecisionsConfig";

export default class KeyDecisionsModel {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {KeyDecisionsConfig} keyDecisionsConfig 
     */
    constructor(dispatcher, keyDecisionsConfig) {
        this._dispatcher = dispatcher;
        this._keyDecisionsConfig = keyDecisionsConfig;

        /** @type {Array<KeyDecision>} */
        this._availableKeyDecisions = [];
        /** @type {Array<KeyDecision>} */
        this._completedKeyDecisions = [];
        /** @type {Array<KeyMomentChoiceContent>} */
        this._lockedOptionsKeyDecisions = [];
    }

    get availableKeyDecisions() {
        return this._availableKeyDecisions;
    }

    get completedKeyDecisions() {
        return this._completedKeyDecisions;
    }

    get lockedOptionsKeyDecisions() {
        return this._lockedOptionsKeyDecisions;
    }

    clearKeyDecisions() {
        this._availableKeyDecisions = [];
        this._completedKeyDecisions = [];
    }

    getNumberKeyDecisionByChapter(chapter) {
        return this._keyDecisionsConfig.getKeyDecisionsByChapter(chapter).length;
    }

    getAvailableKeyDecisionByContent(content) {
        return this._availableKeyDecisions.find(kd => kd.content.getContentID() === content.getContentID());
    }

    /**
     * @param {KeyDecision} keyDecision 
     */
    makeKeyDecisionAvailable(keyDecision) {
        if (!this._availableKeyDecisions.some(kd => kd.id === keyDecision.id)) {
            this._availableKeyDecisions.push(keyDecision);
            const keyDecisionEvent = new KeyDecisionEvent(KeyDecisionEvent.KEY_DECISION_AVAILABLE, keyDecision);
            this._dispatcher.dispatch(keyDecisionEvent.type, { data: keyDecisionEvent });
        } else {
            console.log(`[KeyDecisionsModel][makeKeyDecisionAvailable] Ignoring Key Decision with id ${keyDecision.id} since it already exists in the array`);
        }
        this._dispatcher.dispatch(HubEvent.REFRESH_KEY_DECISIONS_LIST);
    }

    /**
     * @param {KeyDecision} keyDecision 
     */
    completeKeyDecision(keyDecision) {
        if (!this._completedKeyDecisions.some(kd => kd.id === keyDecision.id)) {
            this._completedKeyDecisions.push(keyDecision);

            const keyDecisionEvent = new KeyDecisionEvent(KeyDecisionEvent.KEY_DECISION_COMPLETED, keyDecision);
            this._dispatcher.dispatch(keyDecisionEvent.type, { data: keyDecisionEvent });
        } else {
            console.log(`[KeyDecisionsModel][completeKeyDecision] Ignoring Key Decision with id ${keyDecision.id} since it already exists in the array`);
        }
        this._dispatcher.dispatch(HubEvent.REFRESH_KEY_DECISIONS_LIST);
    }

    /**
     * @param {KeyDecision} keyDecision 
     */
    makeKeyDecisionUnavailable(keyDecision) {
        const keyDecisionIndex = this._availableKeyDecisions.indexOf(keyDecision);
        if (keyDecisionIndex === -1) {
            console.error(`[KeyDecisionsModel] Cannot make a key decision unavailable if it isn't available.`);
            return;
        }

        this._availableKeyDecisions.splice(keyDecisionIndex, 1);

        const keyDecisionEvent = new KeyDecisionEvent(KeyDecisionEvent.KEY_DECISION_UNAVAILABLE, keyDecision);
        this._dispatcher.dispatch(keyDecisionEvent.type, { data: keyDecisionEvent });
    }



    /**
     * @param {KeyMomentChoiceContent} content 
     */
    saveGoodLockedOptionsKeyDecisions(content) {
        if(!!this._lockedOptionsKeyDecisions.includes(content)) {
            return;
        }
        if (content.getType() !== KeyMomentChoiceContent.TYPE || content.getSituationDescription() !== KeyDecisionAnswer.GOOD_RATING) {
            return;
        }
        this._lockedOptionsKeyDecisions.push(content);
    }

    getGoodLockedOptionsKeyDecisions() {
        return this._lockedOptionsKeyDecisions;
    }

    getGoodLockedOptionsKeyDecisionsWithPrefix(prefix) {
        return this._lockedOptionsKeyDecisions.filter(kd => kd.getKeyMomentID().startsWith(prefix));
    }

    toObject() {
        return {
            availableKeyDecisions: this._availableKeyDecisions.map(kd => kd.toObject()),
            completedKeyDecisions: this._completedKeyDecisions.map(kd => kd.toObject()),
            lockedOptionsKeyDecisions: this._lockedOptionsKeyDecisions.map(km => km.convertToObject())
        };
    }

    fromObject(obj, languagePack) {
        this._availableKeyDecisions = obj.availableKeyDecisions.map(a => keyDecisionFromObject(a, languagePack));
        this._completedKeyDecisions = obj.completedKeyDecisions.map(a => keyDecisionFromObject(a, languagePack));
        this._lockedOptionsKeyDecisions = obj.lockedOptionsKeyDecisions.map(a => keyMomentChoiceContentFromObject(a, languagePack));
    }
}

const keyDecisionFromObject = function (obj, languagePack) {
    const keyDecision = new KeyDecision();
    keyDecision.fromObject(obj, languagePack);
    return keyDecision;
}

const keyMomentChoiceContentFromObject = function (obj, languagePack) {
    const keyMomentChoiceContent = new KeyMomentChoiceContent(languagePack, null, null);
    keyMomentChoiceContent.setPropertiesFromObject(obj);
    return keyMomentChoiceContent;
}